import { Copy, Crown, Lock, Pencil, Plus, Puzzle, RefreshCw, ShieldAlert, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, SectionCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatNumber } from '@/lib/utils';

import { PlanDialogo } from '../components/plan-dialogo';
import { PlanDuplicarDialogo } from '../components/plan-duplicar-dialogo';
import { PlanModulosDialogo } from '../components/plan-modulos-dialogo';
import { useMutarPlanes, usePlanes } from '../planes.api';
import { fechaCorta } from '../lib/configuracion-format';
import type { Plan } from '../planes.types';

/** Mensaje de error legible. */
function mensajeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'No se pudo completar la operación.';
}

/** Tarjeta de un plan con su color, precio y módulos. */
function TarjetaPlan({
  plan,
  totalModulos,
  onConfigurar,
  onEditar,
  onDuplicar,
  onEliminar,
}: {
  plan: Plan;
  totalModulos: number;
  onConfigurar: () => void;
  onEditar: () => void;
  onDuplicar: () => void;
  onEliminar: () => void;
}) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="h-1.5" style={{ backgroundColor: plan.color }} aria-hidden="true" />

      <div className="flex min-h-0 flex-1 flex-col px-4 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              {plan.codigo}
            </p>
            <h3 className="truncate text-base font-bold text-ink-900" title={plan.nombre}>
              {plan.nombre}
            </h3>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-1">
            {plan.es_sistema ? (
              <Badge variant="brand" title="Plan del sistema">
                <Lock className="size-3" aria-hidden="true" />
                Sistema
              </Badge>
            ) : null}
            {!plan.activo ? <Badge variant="warning">Inactivo</Badge> : null}
          </div>
        </div>

        {plan.precio_mensual > 0 ? (
          <p className="mt-1 text-lg font-bold tabular-nums text-ink-900">
            {formatCurrency(plan.precio_mensual)}
            <span className="text-xs font-normal text-ink-500"> /mes</span>
          </p>
        ) : (
          <p className="mt-1 text-sm text-ink-500">Sin precio configurado</p>
        )}

        {plan.descripcion ? (
          <p className="mt-1.5 line-clamp-2 text-[13px] leading-relaxed text-ink-500">
            {plan.descripcion}
          </p>
        ) : null}

        <dl className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-control border border-ink-100 bg-ink-50/60 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Módulos</dt>
            <dd className="text-base font-bold tabular-nums text-ink-900">{formatNumber(plan.modulos)}</dd>
          </div>
          <div className="rounded-control border border-ink-100 bg-ink-50/60 px-3 py-2">
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Total</dt>
            <dd className="text-base font-bold tabular-nums text-ink-600">
              {formatNumber(totalModulos)}
            </dd>
          </div>
        </dl>

        <p className="mt-2 text-[11px] text-ink-400">
          Creado el {fechaCorta(plan.fecha_creacion)}
          {plan.max_usuarios > 0 ? ` · ${plan.max_usuarios} usuario(s)` : ' · usuarios ilimitados'}
          {plan.max_terminales > 0 ? ` · ${plan.max_terminales} terminal(es)` : ' · terminales ilimitadas'}
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button size="sm" className="flex-1" onClick={onConfigurar}>
            <Puzzle className="size-3.5" aria-hidden="true" />
            Configurar módulos
          </Button>
          <Button size="icon" variant="outline" title="Editar plan" aria-label={`Editar ${plan.nombre}`} onClick={onEditar}>
            <Pencil className="size-4" aria-hidden="true" />
          </Button>
          <Button size="icon" variant="outline" title="Duplicar plan" aria-label={`Duplicar ${plan.nombre}`} onClick={onDuplicar}>
            <Copy className="size-4" aria-hidden="true" />
          </Button>
          {plan.es_sistema ? (
            <Button size="icon" variant="ghost" disabled title="Plan del sistema: no se puede eliminar">
              <Lock className="size-4" aria-hidden="true" />
            </Button>
          ) : (
            <Button
              size="icon"
              variant="ghost"
              title="Eliminar plan"
              aria-label={`Eliminar ${plan.nombre}`}
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              onClick={onEliminar}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

/**
 * Gestión de Planes (`/configuracion/planes`).
 *
 * Puerto de `Planes::index()`: un plan por tarjeta, con sus módulos, precio y
 * las acciones de crear, editar, duplicar, eliminar y configurar módulos.
 * Igual que en el panel anterior, sólo entra el administrador principal.
 */
export function PlanesPage() {
  useDocumentTitle('Configuración · Gestión de planes');

  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<Plan | null>(null);
  const [duplicando, setDuplicando] = useState<Plan | null>(null);
  const [configurando, setConfigurando] = useState<Plan | null>(null);
  const [porEliminar, setPorEliminar] = useState<Plan | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const planes = usePlanes();
  const mutaciones = useMutarPlanes();

  const sinPermiso = planes.error instanceof ApiError && planes.error.status === 403;

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        <header>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Crown className="size-6 text-brand-500" aria-hidden="true" />
            Gestión de planes
          </h1>
        </header>

        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Solo el administrador principal puede configurar planes.
          </span>
        </Alert>
      </div>
    );
  }

  const lista = planes.data?.planes ?? [];
  const totalModulos = planes.data?.total_modulos ?? 0;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Crown className="size-6 text-brand-500" aria-hidden="true" />
            Gestión de planes
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            Cada plan define qué módulos puede usar la estación. Los planes del sistema
            (REGULAR, PREMIUM y FULL_ENGINE) no se pueden eliminar.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void planes.refetch()}
            loading={planes.isFetching}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Actualizar
          </Button>
          <Button size="sm" onClick={() => setCreando(true)}>
            <Plus className="size-3.5" aria-hidden="true" />
            Nuevo plan
          </Button>
        </div>
      </header>

      {mutaciones.eliminar.isError ? (
        <Alert variant="error" title="No se pudo eliminar el plan">
          {mensajeError(mutaciones.eliminar.error)}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo completar la operación">
          {aviso}
        </Alert>
      ) : null}

      {planes.isPending ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((clave) => (
            <Skeleton key={clave} className="h-72 rounded-card" />
          ))}
        </div>
      ) : planes.isError ? (
        <Alert variant="error" title="No se pudieron cargar los planes">
          <div className="flex flex-wrap items-center gap-3">
            <span>{mensajeError(planes.error)}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void planes.refetch()}
              loading={planes.isFetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : lista.length === 0 ? (
        <SectionCard title="Planes" description="Todavía no hay planes configurados.">
          <p className="rounded-control border border-dashed border-ink-200 px-4 py-12 text-center text-sm text-ink-500">
            Crea el primer plan para empezar.
          </p>
        </SectionCard>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {lista.map((plan) => (
            <TarjetaPlan
              key={plan.id}
              plan={plan}
              totalModulos={totalModulos}
              onConfigurar={() => setConfigurando(plan)}
              onEditar={() => setEditando(plan)}
              onDuplicar={() => setDuplicando(plan)}
              onEliminar={() => {
                setAviso(null);
                setPorEliminar(plan);
              }}
            />
          ))}
        </div>
      )}

      {creando ? <PlanDialogo plan={null} onClose={() => setCreando(false)} /> : null}

      {editando ? <PlanDialogo key={editando.id} plan={editando} onClose={() => setEditando(null)} /> : null}

      {duplicando ? (
        <PlanDuplicarDialogo key={duplicando.id} plan={duplicando} onClose={() => setDuplicando(null)} />
      ) : null}

      {configurando ? (
        <PlanModulosDialogo key={configurando.id} plan={configurando} onClose={() => setConfigurando(null)} />
      ) : null}

      <ConfirmDialog
        open={porEliminar !== null}
        title={`Eliminar el plan ${porEliminar?.nombre ?? ''}`}
        description="Se elimina el plan y sus relaciones con los módulos. Las estaciones que lo tengan asignado dejarán de tenerlo."
        confirmLabel="Sí, eliminar"
        loading={mutaciones.eliminar.isPending}
        onConfirm={() => {
          if (!porEliminar) return;

          mutaciones.eliminar.mutate(porEliminar.id, {
            onError: (error) => setAviso(mensajeError(error)),
            onSettled: () => setPorEliminar(null),
          });
        }}
        onClose={() => setPorEliminar(null)}
      />
    </div>
  );
}
